import { TooltipWrapper } from 'components/TooltipWrapper'
import { LinkIcon, UnlinkIcon } from 'lucide-react'
import { MapFeatureType } from 'pages/siteView/siteMap/contexts/FeatureInteractionContext'
import { ScaleInput } from 'pages/siteView/siteMap/popupMenus/editMode/editFeature/scale/ScaleInput'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import {
  ExtractKeys,
  SiteFeaturePrimitive,
  SiteFeaturePrimitiveKind,
} from 'types'

const ScaleInputs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  max-width: 450px;
  flex-wrap: wrap;
  justify-content: left;
  align-items: center;
`

interface ScaleInputSectionProps {
  feature: SiteFeaturePrimitive
  featureType: MapFeatureType
}

export const ScaleInputSection: FC<ScaleInputSectionProps> = (props) => {
  const { basicProps, advancedProps } = useMemo(() => {
    let basicProps: ExtractKeys<SiteFeaturePrimitive['geometry']>[] = []
    let advancedProps: ExtractKeys<SiteFeaturePrimitive['geometry']>[] = []
    if (props.feature.kind === SiteFeaturePrimitiveKind.Box)
      basicProps = ['width', 'depth', 'height']
    else if (props.feature.kind === SiteFeaturePrimitiveKind.Cylinder) {
      basicProps = ['height']
      advancedProps = ['theta_start', 'theta_end']
    } else if (props.feature.kind === SiteFeaturePrimitiveKind.Sphere) {
      basicProps = ['radius']
      advancedProps = ['theta_start', 'theta_end', 'phi_start', 'phi_end']
    }
    return { basicProps, advancedProps }
  }, [props])

  const [showAdvancedProps, setShowAdvancedProps] = React.useState(false)
  const [linkCylinderRadii, setLinkCylinderRadii] = React.useState(false)

  return (
    <ScaleInputs>
      {basicProps.map((geometryProp) => (
        <ScaleInput
          key={geometryProp}
          feature={props.feature}
          featureType={props.featureType}
          inputId={geometryProp}
          title={geometryProp + ':'}
          geometryProp={geometryProp}
          // @ts-expect-error -- We ensure that the prop is from the same feature-primitive kind as geometry, but ts doesn't know that
          value={(props.feature.geometry[geometryProp] ?? '') as number}
        />
      ))}
      {props.feature.kind === SiteFeaturePrimitiveKind.Cylinder && (
        <>
          <TooltipWrapper
            label={(linkCylinderRadii ? 'Unlink' : 'Link') + ' radii'}
            offset={5}>
            <button
              onDoubleClick={(e) => e.stopPropagation()}
              onClick={() => setLinkCylinderRadii((prev) => !prev)}>
              {linkCylinderRadii ? (
                <LinkIcon size={16} />
              ) : (
                <UnlinkIcon size={16} />
              )}
            </button>
          </TooltipWrapper>
          {linkCylinderRadii ? (
            <ScaleInput
              feature={props.feature}
              featureType={props.featureType}
              inputId={'combined_radius'}
              title={'radius:'}
              geometryProp={['top_radius', 'bottom_radius']}
              value={props.feature.geometry.top_radius}
            />
          ) : (
            <>
              <ScaleInput
                feature={props.feature}
                featureType={props.featureType}
                inputId={'top_radius'}
                title={'top:'}
                geometryProp={'top_radius'}
                value={props.feature.geometry.top_radius}
              />
              <ScaleInput
                feature={props.feature}
                featureType={props.featureType}
                inputId={'bottom_radius'}
                title={'bottom:'}
                geometryProp={'bottom_radius'}
                value={props.feature.geometry.bottom_radius}
              />
            </>
          )}
        </>
      )}
      {advancedProps.length > 0 && (
        <TooltipWrapper
          label={(showAdvancedProps ? 'Hide' : 'Show') + ' advanced properties'}
          offset={5}>
          <button
            onDoubleClick={(e) => e.stopPropagation()}
            onClick={() => setShowAdvancedProps((prev) => !prev)}>
            ...
          </button>
        </TooltipWrapper>
      )}
      {showAdvancedProps &&
        advancedProps.map((geometryProp) => (
          <ScaleInput
            key={geometryProp}
            feature={props.feature}
            featureType={props.featureType}
            inputId={geometryProp}
            title={geometryProp + ':'}
            geometryProp={geometryProp}
            // @ts-expect-error -- We assume that the prop is from the same feature-primitive kind as geometry, but ts doesn't know that
            value={(props.feature.geometry[geometryProp] ?? '') as number}
          />
        ))}
    </ScaleInputs>
  )
}
